import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import Analytics from '../lib/analytics';

// Hook for automatic page view tracking
export const usePageTracking = () => {
  const location = useLocation();

  useEffect(() => {
    // Track page view on route change
    Analytics.trackPageView(location.pathname + location.search);
  }, [location]);
};

// Hook for scroll depth tracking
export const useScrollTracking = () => {
  const trackScrollDepth = useCallback(() => {
    const scrolledPercentage = Math.round(
      (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100
    );

    // Track at specific milestones
    const milestones = [25, 50, 75, 100];
    milestones.forEach(milestone => {
      if (scrolledPercentage >= milestone) {
        // Use sessionStorage to track which milestones have been sent
        const key = `scroll_${milestone}_${window.location.pathname}`;
        if (!sessionStorage.getItem(key)) {
          Analytics.trackScrollDepth(milestone);
          sessionStorage.setItem(key, 'true');
        }
      }
    });
  }, []);

  useEffect(() => {
    let throttleTimer: number | null = null;

    const handleScroll = () => {
      if (throttleTimer !== null) {
        return;
      }

      throttleTimer = window.setTimeout(() => {
        trackScrollDepth();
        throttleTimer = null;
      }, 1000); // Throttle to once per second
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (throttleTimer !== null) {
        clearTimeout(throttleTimer);
      }
    };
  }, [trackScrollDepth]);
};

// Hook for form interaction tracking
export const useFormTracking = (
  formType: 'priority_access' | 'contact' | 'careers'
) => {
  const trackFormStart = useCallback(() => {
    Analytics.trackForm('form_start', { form_type: formType });
  }, [formType]);

  const trackFieldInteraction = useCallback(
    (fieldName: string) => {
      Analytics.trackForm('form_field_interaction', {
        form_type: formType,
        field_name: fieldName,
      });
    },
    [formType]
  );

  const trackFormSubmit = useCallback(() => {
    Analytics.trackForm('form_submit_attempt', { form_type: formType });
  }, [formType]);

  const trackFormSuccess = useCallback(() => {
    Analytics.trackForm('form_submit_success', { form_type: formType });
  }, [formType]);

  const trackFormError = useCallback(
    (errorType: string) => {
      Analytics.trackForm('form_submit_error', {
        form_type: formType,
        error_type: errorType,
      });
    },
    [formType]
  );

  return {
    trackFormStart,
    trackFieldInteraction,
    trackFormSubmit,
    trackFormSuccess,
    trackFormError,
  };
};

// Export Analytics class for direct use
export { Analytics };
export default Analytics;
